import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  suggestTask,
  createTask,
  getTaskRun,
  getTaskRunResult,
  type SuggestTaskResponse,
} from "@/lib/parallel-api";
import { Loader2 } from "lucide-react";
import { useSnapshot } from "valtio";
import { store, actions } from "@/store";
import slug from "slug";
import { toast } from "sonner";

interface EnrichModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnrich: (prompt: string) => void;
}

export function EnrichModal({
  open,
  onOpenChange,
  onEnrich,
}: EnrichModalProps) {
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedTask, setSuggestedTask] =
    useState<SuggestTaskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);
  const snap = useSnapshot(store);
  const columns =
    snap.companies.length > 0
      ? Object.keys(snap.companies[0]).map((key) => slug(key))
      : [];
  console.log("Available columns for enrichment:", columns);

  const validateInputColumns = (task: SuggestTaskResponse): boolean => {
    if (!task.input_schema?.properties) {
      return true; // No input columns required, so it's valid
    }

    const inputColumns = Object.keys(task.input_schema.properties);
    console.log("Suggested input columns:", inputColumns);
    console.log("Available table columns:", columns);

    // Check if all suggested input columns exist in our table
    const missingColumns = inputColumns.filter((col) => !columns.includes(col));
    console.log("Missing columns:", missingColumns);

    return missingColumns.length === 0;
  };

  const getSuggestTaskWithRetry = async () => {
    setIsLoading(true);
    setError(null);
    setSuggestedTask(null);

    const maxRetries = 3;
    let currentRetry = 0;

    while (currentRetry < maxRetries) {
      try {
        console.log(`Attempt ${currentRetry + 1} to get task suggestion`);
        const result = await suggestTask(prompt.trim(), columns);
        console.log("Suggested Task:", result);

        // Validate that input columns match our table columns
        if (validateInputColumns(result)) {
          console.log("Task suggestion validated successfully");
          setSuggestedTask(result);
          actions.setSuggestedTask(result);
          break;
        } else {
          currentRetry++;
          console.log(
            `Input columns don't match table columns. Retry ${currentRetry}/${maxRetries}`
          );

          if (currentRetry >= maxRetries) {
            throw new Error(
              "Failed to generate a task with matching input columns after 3 attempts. Please try rephrasing your prompt."
            );
          }

          // Add a small delay between retries
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } catch (err) {
        if (currentRetry >= maxRetries - 1) {
          setError(
            err instanceof Error ? err.message : "Failed to suggest task"
          );
          break;
        }
        currentRetry++;
        console.log(`Error on attempt ${currentRetry}, retrying...`);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!suggestedTask && prompt.trim() && !isLoading) {
      // Stage 1: Get task suggestion with validation and retry
      await getSuggestTaskWithRetry();
    } else if (suggestedTask && !isEnriching) {
      // Stage 2: Run enrichment
      await runEnrichment();
    }
  };

  const runEnrichment = async () => {
    if (!suggestedTask) return;

    setIsEnriching(true);
    setError(null);
    actions.clearEnrichmentState();

    // Reset modal state and close immediately after starting enrichment
    setPrompt("");
    setSuggestedTask(null);
    setError(null);
    setIsLoading(false);
    setIsEnriching(false);
    actions.setSuggestedTask(null);
    onOpenChange(false);

    try {
      // Track which columns are being created for this enrichment
      const newColumns: string[] = [];

      // Create output columns
      if (suggestedTask.output_schema?.properties) {
        Object.keys(suggestedTask.output_schema.properties).forEach(
          (columnName) => {
            actions.addColumn(columnName);
            newColumns.push(columnName);
          }
        );
      }

      // Store the columns created in this enrichment session
      actions.setLastEnrichmentColumns(newColumns);

      // Log enrichment start
      console.log(
        `Starting enrichment for ${snap.companies.length} companies with prompt: "${prompt}"`
      );
      console.log("Suggested task:", suggestedTask);

      // Process each company
      const taskPromises = snap.companies.map(async (company) => {
        try {
          console.log(
            `Starting enrichment for company: ${company.name} (ID: ${company.id})`
          );

          // Mark new columns as enriching for this company
          if (suggestedTask.output_schema?.properties) {
            Object.keys(suggestedTask.output_schema.properties).forEach(
              (columnName) => {
                actions.setCompanyColumnEnriching(company.id, columnName, true);
              }
            );
          }
          // Prepare input data based on input schema
          const inputData: Record<string, unknown> = {};
          if (suggestedTask.input_schema?.properties) {
            Object.keys(suggestedTask.input_schema.properties).forEach(
              (key) => {
                inputData[key] = company[key] || "";
              }
            );
          }

          // Create task
          const taskSpec = {
            output_schema: {
              json_schema: {
                additionalProperties: false,
                properties: suggestedTask.output_schema?.properties || {},
                required: suggestedTask.output_schema?.required || [],
                type: "object",
              },
            },
          };

          const taskResponse = await createTask(taskSpec, inputData);
          console.log(
            `Task created for ${company.name}, run_id: ${taskResponse.run_id}`
          );

          // Poll for completion
          let taskStatus = await getTaskRun(taskResponse.run_id);
          console.log(`Task status for ${company.name}: ${taskStatus.status}`);
          while (
            taskStatus.status === "pending" ||
            taskStatus.status === "running" ||
            taskStatus.status === "queued"
          ) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            taskStatus = await getTaskRun(taskResponse.run_id);
            console.log(
              `Task status for ${company.name}: ${taskStatus.status}`
            );
          }

          if (taskStatus.status === "completed") {
            const result = await getTaskRunResult(taskResponse.run_id);
            console.log(`Task completed for ${company.name}:`, result);
            console.log(`Result output:`, result.output);
            console.log(`Basis available:`, !!result.output?.basis, result.output?.basis);

            // Update company with results
            if (
              result.output?.content &&
              suggestedTask.output_schema?.properties
            ) {
              Object.keys(suggestedTask.output_schema.properties).forEach(
                (key) => {
                  const value = result.output.content[key];
                  if (value !== undefined) {
                    console.log(`Updating ${company.name} - ${key}: ${value}`);
                    actions.updateCompanyField(company.id, key, String(value));

                    // Store task basis for this enriched field
                    if (result.output.basis) {
                      console.log(`Storing task basis for ${company.name} - ${key}:`, result.output.basis);
                      actions.setTaskBasis(company.id, key, taskResponse.run_id, result.output.basis);
                      console.log(`Successfully stored task basis for ${company.name} - ${key}`);
                    } else {
                      console.log(`No basis available for ${company.name} - ${key}`);
                    }
                  }
                }
              );
            }
          } else {
            console.error(
              `Task failed for ${company.name}. Status: ${taskStatus.status}`
            );
          }

          // Mark new columns as no longer enriching for this company
          if (suggestedTask.output_schema?.properties) {
            Object.keys(suggestedTask.output_schema.properties).forEach(
              (columnName) => {
                actions.setCompanyColumnEnriching(
                  company.id,
                  columnName,
                  false
                );
              }
            );
          }

          // Decrement credits
          actions.decrementCredits(1);
          console.log(
            `Enrichment completed for ${company.name}, credits decremented`
          );
        } catch (err) {
          console.error(`Failed to enrich company ${company.name}:`, err);
          // Mark new columns as no longer enriching even on error
          if (suggestedTask.output_schema?.properties) {
            Object.keys(suggestedTask.output_schema.properties).forEach(
              (columnName) => {
                actions.setCompanyColumnEnriching(
                  company.id,
                  columnName,
                  false
                );
              }
            );
          }
        }
      });

      await Promise.all(taskPromises);
      console.log(
        `Enrichment completed for all ${snap.companies.length} companies`
      );

      // Show success toast with undo option
      const columnNames = newColumns.join(", ");
      toast.success(`Enrichment completed!`, {
        description: `Added ${newColumns.length} new column${
          newColumns.length !== 1 ? "s" : ""
        }: ${columnNames}`,
        action: {
          label: "Undo",
          onClick: () => {
            actions.undoLastEnrichment();
            toast.success("Enrichment undone", {
              description: "All enriched columns have been removed.",
            });
          },
        },
        duration: 10000, // Show for 10 seconds to give user time to undo
      });

      onEnrich(prompt.trim());
    } catch (err) {
      console.error("Enrichment error:", err);
      toast.error("Enrichment failed", {
        description:
          "There was an error during the enrichment process. Please try again.",
      });
    }
  };

  const handleCancel = () => {
    if (!isEnriching) {
      setPrompt("");
      setSuggestedTask(null);
      setError(null);
      setIsLoading(false);
      setIsEnriching(false);
      actions.setSuggestedTask(null);
      actions.clearEnrichmentState();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Enrich Companies</DialogTitle>
          <DialogDescription>
            Enter a prompt to describe how you want to enrich the company data.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Try these examples:
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() =>
                      setPrompt(
                        "Tell me the founding date of this company in YYYY format"
                      )
                    }
                    disabled={isLoading || isEnriching || !!suggestedTask}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Get founding dates
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPrompt(
                        "Tell me if this company is a 'startup' or 'enterprise'"
                      )
                    }
                    disabled={isLoading || isEnriching || !!suggestedTask}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Classify company size
                  </button>
                </div>
              </div>
              <Textarea
                placeholder="Enter your enrichment prompt here..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[120px]"
                autoFocus
                disabled={isLoading || isEnriching || !!suggestedTask}
              />
            </div>

            {error && (
              <div className="p-3 border border-red-200 bg-red-50 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {suggestedTask && (
              <div className="p-4 border border-green-200 bg-green-50 rounded-md">
                <h4 className="font-medium text-green-800 mb-2">
                  {suggestedTask.title || "Suggested Task"}
                </h4>

                <div className="mb-3">
                  <p className="text-sm font-medium text-green-700 mb-2">
                    Input Fields:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedTask.input_schema?.properties ? (
                      Object.keys(suggestedTask.input_schema.properties).map(
                        (key) => (
                          <Badge key={key} variant="secondary">
                            {key}
                          </Badge>
                        )
                      )
                    ) : (
                      <span className="text-sm text-green-600">
                        No input fields specified
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-sm font-medium text-green-700 mb-2">
                    Output Fields:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedTask.output_schema?.properties ? (
                      Object.entries(
                        suggestedTask.output_schema.properties
                      ).map(([key, value]: [string, unknown]) => (
                        <div key={key} className="flex flex-col">
                          <Badge variant="default" className="mb-1">
                            {key}
                          </Badge>
                          {(value as { description?: string })?.description && (
                            <span className="text-xs text-green-600 max-w-48 break-words">
                              {(value as { description: string }).description}
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-sm text-green-600">
                        No output fields specified
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isEnriching && (
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-md">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-600">
                    Enriching companies... Check the table to see individual
                    progress.
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isEnriching}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                (!prompt.trim() && !suggestedTask) || isLoading || isEnriching
              }
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEnriching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading
                ? "Thinking..."
                : isEnriching
                ? "Enriching..."
                : suggestedTask
                ? "Start Enrichment"
                : "Continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
